import { useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'
import type { DateRange } from '@/components/DateRangePicker'

export type { DateRange }

export function isoDaysAgo(days: number): string {
  const d = new Date()
  d.setDate(d.getDate() - days)
  return d.toISOString().slice(0, 10)
}

export function isoToday(): string {
  return new Date().toISOString().slice(0, 10)
}

export function defaultRange(days = 7): DateRange {
  return { from: isoDaysAgo(days), to: isoToday() }
}

export function diffDays(range: DateRange): number {
  const from = new Date(range.from + 'T00:00:00Z')
  const to = new Date(range.to + 'T00:00:00Z')
  const ms = to.getTime() - from.getTime()
  return Math.max(1, Math.round(ms / 86_400_000))
}

export function rangeToParams(range: DateRange): { dateFrom: string; dateTo: string } {
  return { dateFrom: range.from, dateTo: range.to }
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

/** Kanonisches Lesen einer DateRange aus URL-Params (from/to). Ersetzt die
 *  vormals in Dashboard.tsx und HarvestWorkspace.tsx duplizierte Funktion. */
export function parseRangeFromParams(params: URLSearchParams, fallbackDays = 7): DateRange {
  const from = params.get('from')
  const to = params.get('to')
  if (from && to && ISO_DATE_RE.test(from) && ISO_DATE_RE.test(to)) {
    return { from, to }
  }
  return defaultRange(fallbackDays)
}

/** Hook: DateRange aus der URL (from/to) mit Setter, der die URL per replace aktualisiert. */
export function useDateRange(options?: { fallbackDays?: number }): {
  range: DateRange
  setRange: (next: DateRange) => void
} {
  const fallbackDays = options?.fallbackDays ?? 7
  const [params, setParams] = useSearchParams()
  const range = useMemo(() => parseRangeFromParams(params, fallbackDays), [params, fallbackDays])
  function setRange(next: DateRange) {
    const newParams = new URLSearchParams(params)
    newParams.set('from', next.from)
    newParams.set('to', next.to)
    setParams(newParams, { replace: true })
  }
  return { range, setRange }
}
